import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  useTheme,
  InputAdornment,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { tokens } from "../../theme";
import PermissionGuard from "../../components/PermissionGuard";

/* ---------- helpers ---------- */
const show = (v) => (v === null || v === undefined || v === "" ? "-" : v);

// ensure numeric ID for consistent select value
const toNum = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

// normalize option rows from any API shape into {id, name}
const normalizeList = (list, nameKeys = ["name", "fullName", "title"]) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((o) => {
      const id =
        o?.id ?? o?.ID ?? o?.Id ?? o?.roleId ?? o?.warehouseId ?? o?.uuid;
      const nameKey = nameKeys.find((k) => o && o[k] != null);
      const name = o?.[nameKey] ?? o?.label ?? o?.code ?? `#${id ?? "?"}`;
      return { id, name };
    })
    .filter((o) => o.id !== undefined && o.id !== null);
};

// keep current ID selectable even if not in fetched list
function ensureOption(options, id) {
  if (id === "" || id === null || id === undefined) return options || [];
  const nid = Number(id);
  const exists = (options || []).some((o) => Number(o.id) === nid);
  return exists ? options : [{ id: nid, name: `#${nid}` }, ...(options || [])];
}

/* ---------- API ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, 
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
 
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewUser() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    fullName: "",
    userName: "",
    password: "",
    phone: "",
    roleId: "",
    warehouseId: "",
  });
  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // dropdown options
  const [roles, setRoles] = React.useState([]);
  const [warehouses, setWarehouses] = React.useState([]);
  const [optsLoading, setOptsLoading] = React.useState(false);

  // fetch list (cache-busted)
  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/user");
    setLoading(false);
    if (error) {
      setToast({ open: true, msg: "Failed to load users", sev: "error" });
      setRows([]);
      return;
    }
    setRows(Array.isArray(data?.data) ? data.data : []);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // fetch options for dropdowns (cache-busted)
  const loadOptions = React.useCallback(async () => {
    setOptsLoading(true);
    const [roleRes, whRes] = await Promise.all([
      getJSON("/api/role"),
      getJSON("/api/warehouse"),
    ]);
    setOptsLoading(false);

    setRoles(normalizeList(roleRes.data?.data));
setWarehouses(normalizeList(whRes.data?.data));
  }, []);

  React.useEffect(() => {
    // fetch once on mount
    loadOptions();
  }, [loadOptions]);

  // columns to display in table
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 80, hide: true },
    { field: "fullName", headerName: "Full Name", flex: 1, minWidth: 180 },
    { field: "userName", headerName: "Username", flex: 1, minWidth: 160 },
    { field: "phone", headerName: "Phone", width: 140 },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => params.row.role?.name ?? "-"
    },
    {
      field: "warehouse",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.row.warehouse?.name ?? "-"
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>

              <PermissionGuard module="User" action="update">
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={async () => {
                  setEditRow(row);
                  setEditValues({
                    fullName: row.fullName ?? "",
                    userName: row.userName ?? "",
                    password: "",
                    phone: row.phone ?? "",
             roleId: toNum(row.role?.id ?? row.roleId),
warehouseId: toNum(row.warehouse?.id ?? row.warehouseId),
                  });
                  // ensure latest options before opening dialog
                  await loadOptions();
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            </PermissionGuard>

            <PermissionGuard module="User" action="delete">
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            </PermissionGuard>
          </Box>
        );
      },
    },
  ];

  // grid look (theme-consistent)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  // update (optimistic + refetch)
  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    // Build payload exactly as your API expects
    const payload = {
      fullName: String(editValues.fullName ?? "").trim(),
      userName: String(editValues.userName ?? "").trim(),
      phone: String(editValues.phone ?? "").trim(),
      roleId: Number(editValues.roleId),
      // include password only if provided
      ...(editValues.password ? { password: String(editValues.password) } : {}),
      // include warehouseId only if you want it in your API
      ...(editValues.warehouseId !== "" &&
      editValues.warehouseId !== null &&
      editValues.warehouseId !== undefined
        ? { warehouseId: Number(editValues.warehouseId) }
        : {}),
    };

    const { data, error } = await putJSON(`/api/user/${editRow.id}`, payload);
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    // Your PUT may return updated object directly (or wrapped)
    const updated = data?.data || data;
    const merged = updated?.id ? updated : { id: editRow.id, ...payload };

    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? { ...r, ...merged } : r))
    );

    setToast({ open: true, msg: "User updated successfully", sev: "success" });
    setEditOpen(false);
    setEditRow(null);

    // refresh users and dropdown options for freshest names/relations
    await Promise.all([load(), loadOptions()]);
  };

  // delete (optimistic + refetch)
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { data, error } = await deleteJSON(`/api/user/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));

    setToast({
      open: true,
      msg: data?.message || "User deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);

    // refresh list and options
    await Promise.all([load(), loadOptions()]);
  };

  return (
    <>
      <Box m="20px">
        <Header title="USERS" subtitle="View, edit, and manage users" />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Username" value={show(detailRow.userName)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Full Name"
                  value={show(detailRow.fullName)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Phone" value={show(detailRow.phone)} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LabelValue label="Role" value={detailRow.role?.name ?? "-"} />
              </Grid>
              <Grid item xs={12} sm={6}>
              <LabelValue
  label="Warehouse"
  value={detailRow.warehouse?.name ?? "-"}
/>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="Full Name"
              value={editValues.fullName}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, fullName: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Username"
              value={editValues.userName}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, userName: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Password (leave blank to keep)"
              type="password"
              value={editValues.password}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, password: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Phone"
              value={editValues.phone}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, phone: e.target.value }))
              }
              fullWidth
              variant="filled"
            />

            {/* Role dropdown + refresh */}
            <TextField
              label={
                optsLoading
                  ? "Loading roles..."
                  : roles.length
                  ? "Role"
                  : "No roles found (refresh)"
              }
              select
              value={editValues.roleId}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, roleId: toNum(e.target.value) }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh roles">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {ensureOption(roles, editValues.roleId).map((r) => (
                <MenuItem key={r.id} value={Number(r.id)}>
                  {r.name ?? `#${r.id}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Warehouse dropdown + refresh */}
            <TextField
              label={
                optsLoading
                  ? "Loading warehouses..."
                  : warehouses.length
                  ? "Warehouse"
                  : "No warehouses found (refresh)"
              }
              select
              value={editValues.warehouseId}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  warehouseId: toNum(e.target.value),
                }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh warehouses">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {ensureOption(warehouses, editValues.warehouseId).map((w) => (
                <MenuItem key={w.id} value={Number(w.id)}>
                  {w.name ?? `#${w.id}`}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
